import React from "react";
import { Link } from "react-router-dom";
import ArticleLayout, { H2, DataTable } from "./ArticleLayout";

export default function ArticleRetireAt62() {
  return (
    <ArticleLayout
      meta={{
        slug: "retire-at-62-checklist",
        hub: "Retirement Readiness",
        time: "8 min read",
        title: "Retiring at 62: The Complete Checklist | Centennial",
        seoDescription:
          "Thinking about retiring at 62? The complete checklist: the 3-year health insurance gap before Medicare, the 30% Social Security cut, penalty-free withdrawal rules, and a free readiness score.",
        h1: "Retiring at 62: The Complete Checklist",
        hook: "62 is the most popular retirement age in America — and the one with the most hidden traps. Work through this checklist before you give notice.",
        quickAnswer:
          "Retiring at 62 requires clearing four hurdles most people underestimate: (1) a three-year health insurance gap before Medicare at 65, often $10,000–$17,000/year per person; (2) claiming Social Security at 62 permanently cuts your benefit ~30%; (3) your savings must cover a retirement that could run 38 years; and (4) withdrawals before 59½ have penalty rules — though 401(k) money is penalty-free at 62. If your plan survives 5,000 market simulations with all four costs included, you're genuinely ready.",
      }}
    >
      <H2>1. Bridge the health insurance gap (62 → 65)</H2>
      <p>
        Medicare starts at 65, period. Budget for three years of private coverage: ACA
        marketplace plans for a 62-year-old commonly run $800–$1,400/month before subsidies —
        and managing your taxable income can qualify you for large subsidies. COBRA covers up
        to 18 months if you're leaving a job. Full numbers in{" "}
        <Link className="text-[#2B5339] underline" to="/learn/retirement-healthcare-costs">retirement healthcare costs</Link>.
      </p>

      <H2>2. Decide Social Security separately from retirement</H2>
      <p>
        Retiring at 62 does not mean claiming at 62. Claiming at 62 locks in a ~30% cut versus
        your full benefit; bridging from savings and claiming at 67–70 buys a much larger,
        inflation-protected check for life. Run the comparison:{" "}
        <Link className="text-[#2B5339] underline" to="/learn/social-security-claiming-age">62 vs 67 vs 70</Link>.
      </p>

      <H2>3. Size your savings for 38 years, not 25</H2>
      <DataTable
        head={["Annual spending gap", "Savings needed (25–30×)", "Notes"]}
        rows={[
          ["$25,000", "$625k – $750k", "Plus the 62–65 insurance bridge"],
          ["$40,000", "$1.0M – $1.2M", "Most common dual-income target"],
          ["$60,000", "$1.5M – $1.8M", "Lean toward 30× at age 62"],
        ]}
      />
      <p>
        At 62 your horizon to age 100 is 38 years, so use the conservative end (30×, ~3.3%
        withdrawals) — our{" "}
        <Link className="text-[#2B5339] underline" to="/learn/planning-to-100-study">105,000-simulation study</Link>{" "}
        shows why higher withdrawal rates get punished on long horizons.
      </p>

      <H2>4. Know the early-withdrawal rules</H2>
      <p>
        The "rule of 55": if you leave your employer at 55+, that employer's 401(k) is
        penalty-free — and at 62 all 401(k)/IRA withdrawals are penalty-free (ordinary income
        tax still applies). Roth contributions come out tax- and penalty-free anytime. Taxable
        brokerage accounts have no age rules and fill the bridge years efficiently.
      </p>

      <H2>5. The rest of the checklist</H2>
      <p>
        Debt: enter retirement mortgage-free if possible. Taxes: your low-income bridge years
        (62–67) are prime Roth-conversion territory. Estate: will, powers of attorney,
        beneficiary check. Family: make sure your spouse knows the plan. Purpose: retire{" "}
        <em>to</em> something, not just from something. Score all of it in ten minutes with the
        free <Link className="text-[#2B5339] underline" to="/learn/readiness-checklist">50-question readiness checklist</Link>.
      </p>

      <H2>The final test</H2>
      <p>
        Put the whole plan — insurance bridge, delayed Social Security, 38-year horizon — into
        the free <Link className="text-[#2B5339] underline" to="/">Centennial calculator</Link> and
        stress-test it against 5,000 market futures plus 1929 and 2008. If it survives those,
        you're not guessing anymore.
      </p>
    </ArticleLayout>
  );
}
